import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import RRHHLayout from '@/components/RRHHLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Plus, 
  Search, 
  FileText, 
  Edit, 
  Trash2,
  Filter,
  LayoutGrid,
  List,
  Users,
  Circle
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [trabajadores, setTrabajadores] = useState([]);
  const [filteredTrabajadores, setFilteredTrabajadores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('Todos');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, trabajadorId: null });
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'cards'

  useEffect(() => {
    loadTrabajadores();
  }, []);

  useEffect(() => {
    filterTrabajadores();
  }, [trabajadores, searchTerm, statusFilter]);

  const loadTrabajadores = async () => {
    try {
      const response = await axios.get(`${API}/trabajadores`);
      setTrabajadores(response.data);
    } catch (error) {
      toast.error('Error al cargar trabajadores');
    } finally {
      setLoading(false);
    }
  };

  const filterTrabajadores = () => {
    let filtered = trabajadores;

    // Filter by status
    if (statusFilter !== 'Todos') {
      filtered = filtered.filter(t => t.status === statusFilter);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(t => 
        t.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.primer_apellido.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.segundo_apellido.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.numero_identificacion.includes(searchTerm)
      );
    }

    setFilteredTrabajadores(filtered);
  };

  const handleDelete = async () => {
    try {
      const response = await axios.delete(`${API}/trabajadores/${deleteDialog.trabajadorId}`);
      
      const result = response.data;
      const eliminado = result.eliminado || {};
      const preservado = result.preservado || {};
      
      let mensaje = `${result.message}. `;
      
      // Mostrar lo que se preservó (más importante)
      const itemsPreservados = [];
      if (preservado.hoja_pesador > 0) itemsPreservados.push(`${preservado.hoja_pesador} registros de pesador`);
      if (preservado.tareas > 0) itemsPreservados.push(`${preservado.tareas} tareas`);
      if (preservado.entregas_epi > 0) itemsPreservados.push(`${preservado.entregas_epi} entregas de EPIs`);
      if (preservado.documentos_generados > 0) itemsPreservados.push(`${preservado.documentos_generados} documentos generados`);
      
      if (itemsPreservados.length > 0) {
        mensaje += `Registros históricos preservados: ${itemsPreservados.join(', ')}`;
      }
      
      toast.success(mensaje);
      loadTrabajadores();
    } catch (error) {
      console.error('Error al eliminar trabajador:', error);
      if (error.response?.status === 404) {
        toast.error('Trabajador no encontrado');
      } else {
        toast.error(error.response?.data?.detail || 'Error al eliminar trabajador');
      }
    } finally {
      setDeleteDialog({ open: false, trabajadorId: null });
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      'Activo': 'bg-green-100 text-green-800 border-green-200',
      'Despedido': 'bg-red-100 text-red-800 border-red-200',
      'Liquidado': 'bg-blue-100 text-blue-800 border-blue-200',
      'Renunció': 'bg-yellow-100 text-yellow-800 border-yellow-200'
    };
    return <Badge className={`${variants[status]} border`}>{status}</Badge>;
  };

  return (
    <RRHHLayout user={user} onLogout={onLogout}>
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0 }}
          >
          <Card className="p-6 border-0 shadow-md bg-white hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Trabajadores</p>
                <p className="text-3xl font-bold text-emerald-900 mt-1" data-testid="total-trabajadores">
                  {trabajadores.length}
                </p>
              </div>
              <div className="p-3 bg-emerald-100 rounded-xl">
                <Users className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </Card>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
          <Card className="p-6 border-0 shadow-md bg-white hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Activos</p>
                <p className="text-3xl font-bold text-green-700 mt-1" data-testid="activos-count">
                  {trabajadores.filter(t => t.status === 'Activo').length}
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-xl">
                <Users className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </Card>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
          <Card className="p-6 border-0 shadow-md bg-white hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Liquidados</p>
                <p className="text-3xl font-bold text-blue-700 mt-1">
                  {trabajadores.filter(t => t.status === 'Liquidado').length}
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-xl">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </Card>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.3 }}
          >
          <Card className="p-6 border-0 shadow-md bg-white hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Despedidos</p>
                <p className="text-3xl font-bold text-red-700 mt-1">
                  {trabajadores.filter(t => t.status === 'Despedido').length}
                </p>
              </div>
              <div className="p-3 bg-red-100 rounded-xl">
                <Users className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </Card>
          </motion.div>
        </div>

        {/* Actions and Filters */}
        <Card className="p-6 mb-6 border-0 shadow-md bg-white">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex-1 flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Buscar por nombre o identificación..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-48" data-testid="status-filter">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Todos">Todos</SelectItem>
                  <SelectItem value="Activo">Activo</SelectItem>
                  <SelectItem value="Despedido">Despedido</SelectItem>
                  <SelectItem value="Liquidado">Liquidado</SelectItem>
                  <SelectItem value="Renunció">Renunció</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              {/* View Mode Toggle */}
              <div className="flex border rounded-lg overflow-hidden">
                <Button
                  variant={viewMode === 'list' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                  className={viewMode === 'list' ? 'bg-emerald-600 hover:bg-emerald-700' : 'hover:bg-gray-100'}
                  data-testid="view-list-button"
                  title="Vista Lista"
                >
                  <List className="w-4 h-4" />
                </Button>
                <Button
                  variant={viewMode === 'cards' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('cards')}
                  className={viewMode === 'cards' ? 'bg-emerald-600 hover:bg-emerald-700' : 'hover:bg-gray-100'}
                  data-testid="view-cards-button"
                  title="Vista Tarjetas"
                >
                  <LayoutGrid className="w-4 h-4" />
                </Button>
                <Button
                  variant={viewMode === 'bubbles' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('bubbles')}
                  className={viewMode === 'bubbles' ? 'bg-emerald-600 hover:bg-emerald-700' : 'hover:bg-gray-100'}
                  data-testid="view-bubbles-button"
                  title="Vista Burbujas"
                >
                  <Circle className="w-4 h-4" />
                </Button>
              </div>
              <Button 
                onClick={() => navigate('/trabajadores/nuevo')}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="nuevo-trabajador-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Trabajador
              </Button>
            </div>
          </div>
        </Card>

        {/* Trabajadores Table, Cards or Bubbles */}
        {viewMode === 'list' && (
          <Card className="border-0 shadow-md bg-white">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Código Sistema</TableHead>
                    <TableHead>Nombre Completo</TableHead>
                    <TableHead>Identificación</TableHead>
                    <TableHead>Ocupación</TableHead>
                    <TableHead>Lugar de Trabajo</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                        Cargando trabajadores...
                      </TableCell>
                    </TableRow>
                  ) : filteredTrabajadores.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                        No se encontraron trabajadores
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredTrabajadores.map((trabajador) => (
                      <TableRow key={trabajador.id} data-testid={`trabajador-row-${trabajador.id}`}>
                        <TableCell>
                          <span className="font-mono text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                            {trabajador.codigo_sistema || 'N/A'}
                          </span>
                        </TableCell>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <span>{trabajador.nombre} {trabajador.primer_apellido} {trabajador.segundo_apellido}</span>
                            {trabajador.tipo_trabajador === 'temporal' && (
                              <Badge className="bg-yellow-400 text-yellow-900 text-xs">TEMPORAL/OCASIONAL</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {trabajador.tipo_identificacion} - {trabajador.numero_identificacion}
                        </TableCell>
                        <TableCell>{trabajador.ocupacion || '-'}</TableCell>
                        <TableCell>{trabajador.lugar_trabajo || '-'}</TableCell>
                        <TableCell>{getStatusBadge(trabajador.status)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => navigate(`/trabajadores/${trabajador.id}/expediente`)}
                              data-testid={`ver-expediente-${trabajador.id}`}
                              className="border-emerald-200 hover:bg-emerald-50"
                            >
                              <FileText className="w-4 h-4 mr-1" />
                              Expediente
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => navigate(`/trabajadores/${trabajador.id}/editar`)}
                              data-testid={`editar-${trabajador.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setDeleteDialog({ open: true, trabajadorId: trabajador.id })}
                              data-testid={`eliminar-${trabajador.id}`}
                              className="border-red-200 hover:bg-red-50 text-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </Card>
        )}
        
        {viewMode === 'cards' && (
          /* Cards View */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {loading ? (
              <div className="col-span-full text-center py-8 text-gray-500">
                Cargando trabajadores...
              </div>
            ) : filteredTrabajadores.length === 0 ? (
              <div className="col-span-full text-center py-8 text-gray-500">
                No se encontraron trabajadores
              </div>
            ) : (
              filteredTrabajadores.map((trabajador, index) => (
                <motion.div
                  key={trabajador.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  whileHover={{ y: -5 }}
                >
                <Card 
                  className="border-0 shadow-md hover:shadow-xl transition-shadow bg-white overflow-hidden"
                  data-testid={`trabajador-card-${trabajador.id}`}
                >
                  <CardContent className="p-6">
                    {/* Avatar Circle with Profile Picture or Initials */}
                    <div className="flex flex-col items-center mb-4">
                      {trabajador.profile_picture ? (
                        <img 
                          src={`${BACKEND_URL}${trabajador.profile_picture}`} 
                          alt={`${trabajador.nombre} ${trabajador.primer_apellido}`}
                          className="w-24 h-24 rounded-full object-cover shadow-lg mb-3 border-2 border-emerald-200"
                        />
                      ) : (
                        <div className="w-24 h-24 rounded-full bg-gradient-to-br from-emerald-400 to-green-500 flex items-center justify-center shadow-lg mb-3">
                          <span className="text-3xl font-bold text-white">
                            {trabajador.nombre.charAt(0)}{trabajador.primer_apellido.charAt(0)}
                          </span>
                        </div>
                      )}
                      <h3 className="text-xl font-bold text-gray-900 text-center">
                        {trabajador.nombre} {trabajador.primer_apellido}
                      </h3>
                      <p className="text-sm text-gray-600 text-center">{trabajador.segundo_apellido}</p>
                      {trabajador.tipo_trabajador === 'temporal' && (
                        <div className="flex justify-center mt-2">
                          <Badge className="bg-yellow-400 text-yellow-900 text-xs">TEMPORAL/OCASIONAL</Badge>
                        </div>
                      )}
                    </div>

                    {/* Details */}
                    <div className="space-y-3 mb-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">Ocupación</span>
                        <span className="text-sm font-semibold text-gray-900">{trabajador.ocupacion || '-'}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">Departamento</span>
                        <span className="text-sm font-semibold text-gray-900">{trabajador.departamento || '-'}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">Lugar</span>
                        <span className="text-sm font-semibold text-gray-900">{trabajador.lugar_trabajo || '-'}</span>
                      </div>
                      <div className="flex items-center justify-center pt-2">
                        {getStatusBadge(trabajador.status)}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 pt-4 border-t">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(`/trabajadores/${trabajador.id}/expediente`)}
                        className="flex-1 border-emerald-200 hover:bg-emerald-50 text-emerald-700"
                        data-testid={`ver-expediente-card-${trabajador.id}`}
                      >
                        <FileText className="w-4 h-4 mr-1" />
                        Expediente
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(`/trabajadores/${trabajador.id}/editar`)}
                        className="border-blue-200 hover:bg-blue-50 text-blue-700"
                        data-testid={`editar-card-${trabajador.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setDeleteDialog({ open: true, trabajadorId: trabajador.id })}
                        className="border-red-200 hover:bg-red-50 text-red-600"
                        data-testid={`eliminar-card-${trabajador.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
                </motion.div>
              ))
            )}
          </div>
        )}
        
        {viewMode === 'bubbles' && (
          /* Bubbles View - Similar to Speechify voice selection */
          <div className="py-8">
            {loading ? (
              <div className="text-center py-8 text-gray-500">
                Cargando trabajadores...
              </div>
            ) : filteredTrabajadores.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No se encontraron trabajadores
              </div>
            ) : (
              <div className="relative min-h-[600px] flex flex-col items-center justify-center">
                {/* Floating bubbles on the sides (decorative - smaller) */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                  {/* Left side floating bubbles */}
                  {filteredTrabajadores.slice(0, Math.min(4, Math.floor(filteredTrabajadores.length * 0.2))).map((trabajador, idx) => (
                    <motion.div
                      key={`left-${trabajador.id}`}
                      className="absolute"
                      style={{
                        left: `${5 + idx * 4}%`,
                        top: `${20 + idx * 18}%`,
                      }}
                      initial={{ opacity: 0, scale: 0.5 }}
                      animate={{ 
                        opacity: 0.6, 
                        scale: 1,
                        y: [0, -10, 0],
                      }}
                      transition={{ 
                        duration: 3 + idx * 0.5,
                        repeat: Infinity,
                        delay: idx * 0.3 
                      }}
                    >
                      {trabajador.profile_picture ? (
                        <img 
                          src={`${BACKEND_URL}${trabajador.profile_picture}`} 
                          alt=""
                          className="w-12 h-12 md:w-16 md:h-16 rounded-full object-cover opacity-50 grayscale"
                        />
                      ) : (
                        <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 flex items-center justify-center opacity-50">
                          <span className="text-sm font-bold text-white">
                            {trabajador.nombre.charAt(0)}
                          </span>
                        </div>
                      )}
                    </motion.div>
                  ))}
                  
                  {/* Right side floating bubbles */}
                  {filteredTrabajadores.slice(Math.max(0, filteredTrabajadores.length - 4)).map((trabajador, idx) => (
                    <motion.div
                      key={`right-${trabajador.id}`}
                      className="absolute"
                      style={{
                        right: `${5 + idx * 4}%`,
                        top: `${25 + idx * 16}%`,
                      }}
                      initial={{ opacity: 0, scale: 0.5 }}
                      animate={{ 
                        opacity: 0.6, 
                        scale: 1,
                        y: [0, 10, 0],
                      }}
                      transition={{ 
                        duration: 2.5 + idx * 0.4,
                        repeat: Infinity,
                        delay: idx * 0.2 
                      }}
                    >
                      {trabajador.profile_picture ? (
                        <img 
                          src={`${BACKEND_URL}${trabajador.profile_picture}`} 
                          alt=""
                          className="w-10 h-10 md:w-14 md:h-14 rounded-full object-cover opacity-50 grayscale"
                        />
                      ) : (
                        <div className="w-10 h-10 md:w-14 md:h-14 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 flex items-center justify-center opacity-50">
                          <span className="text-xs font-bold text-white">
                            {trabajador.nombre.charAt(0)}
                          </span>
                        </div>
                      )}
                    </motion.div>
                  ))}
                </div>
                
                {/* Main bubbles grid in center */}
                <div className="relative z-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-8 max-w-5xl mx-auto px-4">
                  {filteredTrabajadores.map((trabajador, index) => (
                    <motion.div
                      key={trabajador.id}
                      className="flex flex-col items-center cursor-pointer group"
                      initial={{ opacity: 0, scale: 0.5 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.4, delay: index * 0.05 }}
                      whileHover={{ scale: 1.1 }}
                      onClick={() => navigate(`/trabajadores/${trabajador.id}/expediente`)}
                      data-testid={`bubble-${trabajador.id}`}
                    >
                      {/* Bubble container with glow effect */}
                      <div className="relative">
                        {trabajador.profile_picture ? (
                          <img 
                            src={`${BACKEND_URL}${trabajador.profile_picture}`} 
                            alt={`${trabajador.nombre} ${trabajador.primer_apellido}`}
                            className="w-20 h-20 md:w-24 md:h-24 lg:w-28 lg:h-28 rounded-full object-cover shadow-lg border-4 border-white group-hover:border-emerald-300 transition-all duration-300 group-hover:shadow-emerald-200 group-hover:shadow-xl"
                          />
                        ) : (
                          <div className="w-20 h-20 md:w-24 md:h-24 lg:w-28 lg:h-28 rounded-full bg-gradient-to-br from-emerald-400 to-green-500 flex items-center justify-center shadow-lg border-4 border-white group-hover:border-emerald-300 transition-all duration-300 group-hover:shadow-emerald-200 group-hover:shadow-xl">
                            <span className="text-2xl md:text-3xl lg:text-4xl font-bold text-white">
                              {trabajador.nombre.charAt(0)}{trabajador.primer_apellido.charAt(0)}
                            </span>
                          </div>
                        )}
                        
                        {/* Status indicator badge */}
                        <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-2 border-white ${
                          trabajador.status === 'Activo' ? 'bg-green-500' :
                          trabajador.status === 'Vacaciones' ? 'bg-yellow-500' :
                          trabajador.status === 'Incapacidad' ? 'bg-orange-500' :
                          trabajador.status === 'Suspendido' ? 'bg-red-500' :
                          'bg-gray-400'
                        }`} />
                        
                        {/* Play/Action button on hover */}
                        <motion.div 
                          className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                          whileHover={{ scale: 1.1 }}
                        >
                          <div className="w-8 h-8 rounded-full bg-black/80 flex items-center justify-center shadow-lg">
                            <FileText className="w-4 h-4 text-white" />
                          </div>
                        </motion.div>
                      </div>
                      
                      {/* Name and role */}
                      <div className="mt-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <p className="font-semibold text-gray-900 text-sm md:text-base">
                            {trabajador.nombre}
                          </p>
                          {trabajador.status === 'Activo' && (
                            <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {trabajador.ocupacion || trabajador.departamento || 'Sin asignar'}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
                
                {/* Bottom info */}
                <div className="mt-10 text-center">
                  <p className="text-sm text-gray-400">
                    {filteredTrabajadores.length} trabajadores disponibles
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, trabajadorId: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              ¿Eliminar trabajador?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p className="font-medium text-gray-900">
                Esta acción no se puede deshacer.
              </p>
              
              <div>
                <p className="text-sm font-semibold text-red-700 mb-1">
                  Se eliminará:
                </p>
                <ul className="text-sm list-disc list-inside space-y-1 text-gray-700">
                  <li>El trabajador y su información básica</li>
                  <li>Archivos físicos (foto de perfil, documentos)</li>
                  <li>Notificaciones temporales</li>
                </ul>
              </div>

              <div className="bg-emerald-50 border border-emerald-200 rounded p-2">
                <p className="text-sm font-semibold text-emerald-900 mb-1">
                  ✓ Se preservarán (NO se eliminan):
                </p>
                <ul className="text-xs list-disc list-inside space-y-1 text-emerald-800">
                  <li>Registros de Hoja del Pesador</li>
                  <li>Tareas asignadas (para cálculo de costos)</li>
                  <li>Entregas de EPIs (control de inventario)</li>
                  <li>Documentos generados (contratos - auditoría legal)</li>
                </ul>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded p-2">
                <p className="text-xs text-blue-800">
                  <strong>ℹ️ Nota:</strong> Los registros históricos se mantienen para auditoría, 
                  cálculo de costos y cumplimiento legal.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete">Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete} 
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete"
            >
              Sí, eliminar trabajador
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </RRHHLayout>
  );
}
